import surgerySelect from "@/mixins/surgerySelect.json";
import moment from "moment";
import {
  UNIQUE_INSURANCE_CODES,
  implantsTriggerSurgeryCodes
} from "@/utilities/surgery-questions.js";
import { createEmptyPatientRequests } from "@/utilities/patient-requests";

const ANESTHESIA_PRIMARY_CODES = ["5205", "5215", "5216", "5203", "5204"];
const ANESTHESIA_SECONDARY_CODES = ["5230", "5220"];

const DEFAULT_STATE = {
  isEditMode: false,
  surgeryId: null,
  // Steps
  currentStep: 1,
  // Surgery data
  surgery: {
    id_num: "",
    passport_type: "",
    first_name: "",
    last_name: "",
    birth_date: null,
    parent_name: "",
    parental_status: "",
    phone: "",
    phone2: "",
    lang: "",
    gender: "",
    email: "",
    selected_surgery: [],
    initial_selected_surgery: [],
    treatment_history: "",
    current_diagnosis: [],
    past_diagnosis: [],
    background_diagnosis: "",
    phisycal_check: "",
    no_drug_sens: false,
    no_material_sens: false,
    medication_sensitivity: [],
    material_sensitivity: [],
    insurance_surgery: [],
    assistant: "",
    assistant_name: "",
    hospitalization: "",
    anesthesia: "",
    xray_type: "",
    select_frozen_cut: "",
    requests: {
      monitoring_request: false,
      blood: false,
      icu: false,
      tee_request: false,
      navigation_request: false
    },
    doctor_fee: "",
    insurance_type: "",
    resources: "",
    notes: "",
    files: [],
    isInsuranceCompany: "",
    insuranceCompany: "",
    handle_insurance_claim_by_hmc: "",
    confirm_agreement: "",
    pre_surgery_override: false,
    // Anesthesia indication data
    anesthesia_indication: null,
    implantsData: [],
    payment_token: "",
    patient_requests: createEmptyPatientRequests(),
    // Request types already sent to the hospital, so they are not sent twice
    sent_patient_request_types: []
  },
  // Insurance form state (managed separately for component isolation)
  insuranceForm: {
    payType: "",
    isPayRequired: ""
  },
  // Temp storage for tracking insurance data per surgery during form editing
  insuranceSurgeryTemp: [],
  // Insurance-related state
  isInsuranceType: false,
  isInsuranceRowsLoading: false,
  insuranceSurgeryCategories: [],
  // Static insurance types from surgerySelect.json
  insuranceTypes: surgerySelect.insurance_types,
  implantsList: []
};

const Surgery = {
  namespaced: true,
  state: () => JSON.parse(JSON.stringify(DEFAULT_STATE)),
  mutations: {
    SET_IS_EDIT_MODE(state, value) {
      state.isEditMode = value;
    },
    SET_SURGERY_ID(state, id) {
      state.surgeryId = id;
    },
    SET_CURRENT_STEP(state, step) {
      state.currentStep = step;
      window.scrollTo(0, 0);
    },
    SET_SURGERY(state, surgery) {
      Object.assign(state.surgery, surgery);
    },
    SET_INSURANCE_FORM(state, payload) {
      Object.assign(state.insuranceForm, payload);
    },
    SET_INSURANCE_SURGERY_TEMP(state, payload) {
      state.insuranceSurgeryTemp = payload;
    },
    /**
     * Updates a single item in insuranceSurgeryTemp at the given index
     */
    UPDATE_INSURANCE_SURGERY_TEMP_ITEM(state, { index, data }) {
      if (state.insuranceSurgeryTemp[index]) {
        state.insuranceSurgeryTemp[index] = {
          ...state.insuranceSurgeryTemp[index],
          ...data
        };
        // Trigger reactivity
        state.insuranceSurgeryTemp = [...state.insuranceSurgeryTemp];
      }
    },
    SET_IS_INSURANCE_TYPE(state, value) {
      state.isInsuranceType = value;
    },
    SET_IS_INSURANCE_ROWS_LOADING(state, value) {
      state.isInsuranceRowsLoading = value;
    },
    SET_INSURANCE_SURGERY_CATEGORIES(state, categories) {
      state.insuranceSurgeryCategories = categories;
    },
    /**
     * Resets insurance-related fields when insurance type changes
     */
    RESET_INSURANCE_FIELDS(state) {
      state.insuranceForm.payType = "";
      state.insuranceForm.isPayRequired = "";
      state.surgery.doctor_fee = "";
      state.surgery.handle_insurance_claim_by_hmc = "";
    },
    // /**
    //  * Sets the implants list
    //  */
    SET_IMPLANTS_LIST(state, implants) {
      state.implantsList = implants;
    },
    /**
     * Resets the entire store state to initial values
     */
    RESET_STATE(state) {
      Object.assign(state, JSON.parse(JSON.stringify(DEFAULT_STATE)));
    }
  },
  getters: {
    /**
     * Checks if the selected pay type is a care service option
     */
    isPayTypeCareService: state => {
      const careServiceOptions = [
        "כללית מושלם",
        // "לאומית שבן",
        "מאוחדת שבן",
        "משרד הביטחון"
      ];
      return careServiceOptions.includes(state.insuranceForm.payType);
    },

    /**
     * Finds special insurance code for RHINOPLASTY case
     * Returns the insurance type name if found, null otherwise
     */
    foundSpecialInsuranceCode: state => {
      if (!Array.isArray(state.insuranceSurgeryTemp)) return null;

      const item = state.insuranceSurgeryTemp.find(s => s && s.insurance_type);
      if (!item) return null;

      const insurance =
        item.insurance_type === "חברת ביטוח"
          ? state.surgery.insuranceCompany
          : item.insurance_type;

      return insurance;
    },

    /**
     * Checks if RHINOPLASTY surgery with private insurance is selected
     */
    isRHINOPLASTYSurgery: state => {
      if (
        !state.insuranceSurgeryTemp?.length ||
        state.insuranceForm.payType !== "כללית מושלם"
      ) {
        return false;
      }
      return state.insuranceSurgeryTemp.some(
        s =>
          s &&
          s.code_name_hospital === "1 > RHINOPLASTY" &&
          s.insurance_type === "פרטי"
      );
    },

    hasImplantsTriggerCode: state => {
      return state.insuranceSurgeryTemp?.some(s => {
        if (!s?.code_name_hospital) return false;
        const code = s.code_name_hospital.split(" > ")[0];
        return implantsTriggerSurgeryCodes.includes(code);
      });
    },

    showImplantsSection: (state, getters) => {
      return !!state.implantsList?.length && getters.hasImplantsTriggerCode;
    },

    stepsNumber: (state, getters) => {
      return getters.showImplantsSection ? 7 : 6;
    },

    /**
     * Checks if the anesthesia indication is required for the surgery
     */
    isAnesthesiaIndicationRequired: state => {
      // Check if patient is under 18 years old
      const birthDate = state.surgery?.birth_date;
      if (birthDate) {
        const age = moment().diff(moment(birthDate, "DD/MM/YYYY"), "years");
        if (age < 18) {
          return false;
        }
      }

      // Check if pay type is כללית מושלם
      if (
        !state.insuranceSurgeryTemp?.length ||
        state.insuranceForm.payType !== "כללית מושלם"
      ) {
        return false;
      }

      const hasCodeStartingWith = codes =>
        state.insuranceSurgeryTemp.some(
          surgery =>
            surgery &&
            codes.some(code => surgery.code_name_hospital?.startsWith(code))
        );

      const hasPrimaryCode = hasCodeStartingWith(ANESTHESIA_PRIMARY_CODES);
      const hasSecondaryCode = hasCodeStartingWith(ANESTHESIA_SECONDARY_CODES);

      return hasPrimaryCode && hasSecondaryCode;
    },

    /**
     * Gets an insurance type by name
     */
    getInsuranceByName: state => name => {
      return state.insuranceTypes.find(t => t.insurance_name === name);
    },

    /**
     * Builds the surgery object in the format expected by SurgApprove and the API
     * Maps internal field names to API field names
     */
    surgeryForApproval: (state, getters, rootState, rootGetters) => {
      const s = state.surgery;
      const payType = state.insuranceForm.payType;
      const isPayTypeCareService = getters.isPayTypeCareService;
      const activeDoctor = rootGetters.activeDoctor;

      // Helper function to get full care services name
      const getFullCareServices = type => {
        const careServicesMap = {
          כללית: "כללית מושלם",
          מאוחדת: "מאוחדת שבן",
          מכבי: "מכבי שלי",
          לאומית: "לאומית שבן"
        };
        return careServicesMap[type] || type;
      };

      const isHealthy = s.background_diagnosis === "healthy";

      // Build the surgery object
      const surgeryObj = {
        form_type: "modal",
        surgery_id: state.surgeryId || "",
        block_id: "",
        start_surgery: "",
        end_surgery: "",
        doctor_id: activeDoctor?.id,
        passport: s.id_num,
        passport_type: s.passport_type,
        first_name: s.first_name,
        last_name: s.last_name,
        birth_date: moment(s.birth_date, "DD/MM/YYYY").format("DD-MM-YYYY"),
        gender: s.gender,
        phone: s.phone,
        user_language: s.lang,
        second_phone: s.phone2,
        apotropos_name: s.parent_name,
        parental_status: s.parental_status,
        email: s.email,
        current_surgery: [],
        indication: s.treatment_history,
        current_diagnosis: [],
        checkbox_past_diagnosis: isHealthy ? 1 : 0,
        past_diagnosis: [],
        physical_examination: s.phisycal_check,
        checkbox_medication_sensitivity: s.no_drug_sens ? 1 : null,
        medication_sensitivity: [],
        checkbox_material_sensitivity: s.no_material_sens ? 1 : null,
        material_sensitivity: [],
        insurance_surgery: [],
        insurance_name: "",
        secondary_insurance_name: "",
        doctor_fee: s.doctor_fee || 0,
        assistant_status: s.assistant,
        assistant_name: s.assistant_name,
        hospitalization: s.hospitalization,
        resources: s.resources || "",
        notes: s.notes,
        frozen_cut: s.select_frozen_cut,
        xray_type: s.xray_type,
        monitoring_request: s.requests?.monitoring_request ? "כן" : "",
        blood: s.requests?.blood ? "כן" : "",
        icu: s.requests?.icu ? "כן" : "",
        tee_request: s.requests?.tee_request ? "כן" : "",
        navigation_request: s.requests?.navigation_request ? "כן" : "",
        anesthesia: s.anesthesia,
        handle_insurance_claim_by_hmc: s.handle_insurance_claim_by_hmc,
        pre_surgery_override: s.pre_surgery_override
      };

      // Set insurance names
      if (isPayTypeCareService || payType === "") {
        surgeryObj.insurance_name = s.insurance_type;
        if (isPayTypeCareService) {
          surgeryObj.insurance_name = payType;
        }
        surgeryObj.secondary_insurance_name = s.insuranceCompany;

        if (isPayTypeCareService && payType === "משרד הביטחון") {
          surgeryObj.secondary_insurance_name = s.insurance_type;
        }

        if (
          payType === "כללית מושלם" &&
          getters.foundSpecialInsuranceCode &&
          state.insuranceSurgeryTemp.length === 1
        ) {
          surgeryObj.secondary_insurance_name = surgeryObj.insurance_name;
          surgeryObj.insurance_name = getters.foundSpecialInsuranceCode;
        }
      } else if (payType === "חברת ביטוח") {
        surgeryObj.insurance_name = s.insuranceCompany;
        surgeryObj.secondary_insurance_name = getFullCareServices(
          s.insurance_type
        );
      } else {
        surgeryObj.insurance_name = payType;
        if (s.insurance_type === "ללא קופה") {
          surgeryObj.secondary_insurance_name = s.insuranceCompany;
        } else {
          if (payType === "מכבי" || payType === "לאומית") {
            surgeryObj.secondary_insurance_name = s.insuranceCompany;
          } else {
            surgeryObj.secondary_insurance_name = getFullCareServices(
              s.insurance_type
            );
          }
        }
      }

      if (s.implantsData?.length) {
        surgeryObj.custom_implant = "";
        surgeryObj.implants = [];
        s.implantsData.forEach(item => {
          if (item.supplier_id === "other") {
            if (!surgeryObj.custom_implant) {
              surgeryObj.custom_implant = item.implantName;
            } else {
              surgeryObj.custom_implant += ", " + item.implantName;
            }
          } else {
            surgeryObj.implants.push(item.generic_item_id);
          }
        });
      }

      // Map selected surgeries to current_surgery format

      s.selected_surgery?.forEach(surgery => {
        surgeryObj.current_surgery.push({
          is_primary: surgery.is_primary,
          procedure: surgery.code_name_clinical,
          surgery_name: surgery.code_name_hospital,
          surgery_duration: surgery.duration,
          default_surgery_duration: surgery.default_surgery_duration,
          side: surgery.side
        });
      });

      // Map current diagnosis
      s.current_diagnosis?.forEach(diagnosis => {
        surgeryObj.current_diagnosis.push({
          side: diagnosis.side,
          diagnosis_number: diagnosis.code,
          diagnosis_name: diagnosis.name,
          notice: diagnosis.notes
        });
      });

      // Map past diagnosis
      s.past_diagnosis?.forEach(diagnosis => {
        surgeryObj.past_diagnosis.push({
          side: diagnosis.side,
          diagnosis_number: diagnosis.diagnosis_number,
          diagnosis_name: diagnosis.diagnosis_name,
          notice: diagnosis.notice
        });
      });

      // Map medication sensitivity
      s.medication_sensitivity?.forEach(sens => {
        surgeryObj.medication_sensitivity.push({
          response: sens.reaction,
          severity: sens.severity,
          medication: sens.name,
          notice: sens.notes
        });
      });

      // Map material sensitivity
      s.material_sensitivity?.forEach(material => {
        surgeryObj.material_sensitivity.push({
          response: material.reaction,
          severity: material.severity,
          material_name: material.name,
          notice: material.notes
        });
      });

      // Map insurance surgery temp
      state.insuranceSurgeryTemp?.forEach(insurance => {
        let type = insurance?.insurance_type;

        if (!type) {
          type = isPayTypeCareService ? payType : s.insurance_type;
        }

        surgeryObj.insurance_surgery.push({
          insurance: type,
          surgery_name: insurance?.code,
          notice: insurance?.notes
        });
      });

      // If it is company insurance delete insurance_surgery
      if (!isPayTypeCareService) surgeryObj.insurance_surgery = [];

      return surgeryObj;
    }
  },
  actions: {
    /**
     * Fetches insurance data after pay type changes
     * Sets isInsuranceType flag and fetches insurance surgery codes for each surgery
     * @param {string} payTypeValue - The selected pay type value
     */
    async getDataAfterPayTypeChanged(
      { state, commit, getters, dispatch },
      payTypeValue
    ) {
      commit("SET_IS_INSURANCE_TYPE", false);

      const selectedInsurance = getters.getInsuranceByName(payTypeValue);
      if (!selectedInsurance) return;

      // Only process unique insurance codes that require surgery code mapping
      if (!UNIQUE_INSURANCE_CODES.includes(selectedInsurance.insurance_code)) {
        return;
      }

      commit("SET_IS_INSURANCE_TYPE", true);

      const insuranceId = parseInt(selectedInsurance.insurance_code);

      // Check if we need to fetch insurance surgery codes
      const isInsurance =
        state.isInsuranceType &&
        (getters.isPayTypeCareService ||
          state.surgery.isInsuranceCompany === "false") &&
        state.insuranceSurgeryTemp?.length;

      if (!isInsurance) return;

      // Fetch insurance codes for each surgery in temp array
      for (let i = 0; i < state.insuranceSurgeryTemp.length; i++) {
        await dispatch("setInsuranceCode", { index: i, insuranceId });
      }
    },

    /**
     * Fetches and sets insurance surgery codes for a specific surgery row
     * @param {Object} payload
     * @param {number} payload.index - Index in insuranceSurgeryTemp array
     * @param {number} payload.insuranceId - Insurance ID to fetch codes for
     */
    async setInsuranceCode({ state, commit }, { index, insuranceId }) {
      const currentRow = state.insuranceSurgeryTemp[index];
      if (!currentRow?.code_name_hospital) return;

      commit("SET_IS_INSURANCE_ROWS_LOADING", true);

      try {
        const [code] = currentRow.code_name_hospital.split(" > ");

        // Try to fetch insurance surgery codes
        let res = await this._vm.$API.getTypehead(
          "insurance_surgery",
          null,
          insuranceId,
          null,
          null,
          code
        );

        // If no results, try alternative endpoint
        if (!res?.length) {
          res = await this._vm.$API.getTypehead(
            "insurance_appointment",
            null,
            insuranceId,
            null,
            null,
            code
          );
        }

        const insuranceSurgeryCodes = res?.map(r => r.surgery_name) || [];

        // Determine the code to set
        let codeToSet = "";
        if (insuranceSurgeryCodes.length === 1) {
          // If only one result, auto-select it
          codeToSet = insuranceSurgeryCodes[0];
        } else if (currentRow.code) {
          // If result already set, check if it's still valid
          const found = insuranceSurgeryCodes.find(i => i === currentRow.code);
          codeToSet = found ? currentRow.code : "";
        }

        // Update the temp item
        commit("UPDATE_INSURANCE_SURGERY_TEMP_ITEM", {
          index,
          data: {
            insurance_surgery_codes: insuranceSurgeryCodes,
            code: codeToSet
          }
        });
      } catch (error) {
        console.error("Failed to fetch insurance surgery codes:", error);
      } finally {
        commit("SET_IS_INSURANCE_ROWS_LOADING", false);
      }
    },

    /**
     * Handles special insurance code change (for RHINOPLASTY cases)
     * @param {Object} payload
     * @param {number} payload.index - Index of the surgery
     * @param {string} payload.value - Selected insurance type value
     */
    async handleSpecialInsuranceCodeChange(
      { state, commit, dispatch },
      { index, value }
    ) {
      if (!value) return;

      // If it's insurance company, use the selected company name
      let insuranceName = value;
      if (value === "חברת ביטוח") {
        insuranceName = state.surgery.insuranceCompany;
      }

      const selectedInsurance = state.insuranceTypes.find(
        t => t.insurance_name === insuranceName
      );
      if (!selectedInsurance) return;

      // Update the flag for special insurance code
      if (state.insuranceSurgeryTemp[index]) {
        commit("UPDATE_INSURANCE_SURGERY_TEMP_ITEM", {
          index,
          data: {
            newSpecialInsuranceCode:
              selectedInsurance.insurance_name !== "כללית מושלם"
          }
        });
      }

      const insuranceId = parseInt(selectedInsurance.insurance_code);
      await dispatch("setInsuranceCode", { index, insuranceId });
    },

    /**
     * Formats orders data for API calls
     * @param {Object} context
     * @param {Object} payload
     * @param {Array} payload.orders - Array of surgery orders to format
     * @param {number} payload.index - Optional specific index for insurance data
     * @returns {Array} Formatted orders ready for API
     */
    getFormattedOrders({ state, getters }, { orders, index = undefined }) {
      const payType = state.insuranceForm.payType;
      const type =
        payType === "חברת ביטוח" ? state.surgery.insuranceCompany : payType;
      const selectedInsurance = getters.getInsuranceByName(type);

      const formattedOrders = orders.map((s, i) => {
        const [tafnit_code, tafnit_name] = s.code_name_hospital.split(" > ");
        const [icd9_code, icd9_name] = s.code_name_clinical.split(" > ");
        const duration_sec = moment.duration(s.duration).asSeconds();
        const default_duration_sec = moment
          .duration(s.default_surgery_duration)
          .asSeconds();
        const isSpecialCode = tafnit_code == 1 && payType === "כללית מושלם";

        let tmp = {
          tafnit_code,
          tafnit_name,
          icd9_code,
          icd9_name,
          duration_sec,
          default_duration_sec,
          body_side: s.side,
          insurance_id: selectedInsurance?.insurance_code,
          insurance_name: selectedInsurance?.insurance_name,
          item_code: null,
          name: null,
          medical_notes: null
        };
        if (s.is_primary) tmp.is_primary = "primary";

        // If exist selected insurance set to order all details
        if (state.insuranceSurgeryTemp?.length) {
          let insurance = state.insuranceSurgeryTemp[index];
          if (!insurance) insurance = state.insuranceSurgeryTemp[i];

          let insuranceType = payType;
          if (isSpecialCode && insurance)
            insuranceType = insurance.insurance_type;

          insuranceType =
            insuranceType === "חברת ביטוח"
              ? state.surgery.insuranceCompany
              : insuranceType;

          const insuranceForCode = getters.getInsuranceByName(insuranceType);

          if (state.isInsuranceType && insurance?.code) {
            const [item_code, name] = insurance.code.split(" > ");
            tmp.item_code = item_code;
            tmp.name = name;
          }
          tmp.insurance_id = insuranceForCode?.insurance_code;
          tmp.insurance_name = insuranceForCode?.insurance_name;
          tmp.medical_notes = insurance?.notes;
        }

        return tmp;
      });

      return formattedOrders;
    },

    /**
     * Calls the surgery orders API
     * @param {Object} context
     * @param {Object} params - API call parameters
     * @returns {Promise<boolean>} Success status
     */
    async callSurgeryOrdersAPI(_, params) {
      try {
        const res = await this._vm.$API.scheduleSurgeryOrders(params);
        if (res && res.status === "ERROR") {
          this._vm.$modal.show(() => import("@/components/Popups/Error.vue"), {
            title: res.message
          });
          return false;
        }
        return true;
      } catch (err) {
        let errorMessage = err.message || "התרחשה שגיאה";
        if (err.message === "לא התקבל קוד בית חולים או קוד ICD9") {
          errorMessage =
            "קוד בית חולים / ICD9 שגוי, יש לחזור ולבחור בקוד המתאים לפעולה.";
        }
        this._vm.$modal.show(() => import("@/components/Popups/Error.vue"), {
          title: errorMessage
        });
        return false;
      }
    },

    /**
     * Fetches and sets initial surgery orders from API
     * @param {Object} context
     * @param {string} surgeryId - The surgery ID to fetch orders for
     */
    async setInitialSurgeryOrders({ commit }, surgeryId) {
      const resOrders = await this._vm.$API.getSurgeryOrders(surgeryId);
      if (!resOrders || resOrders.status !== "OK" || !resOrders.data?.orders) {
        return;
      }

      let selected_surgery = [];
      const current_surgery = resOrders.data?.orders;

      current_surgery.forEach(s => {
        if (s.tafnit_code === "9000") return;

        const duration = s.duration
          ? moment.utc(s.duration * 1000).format("HH:mm")
          : "HH:mm";

        selected_surgery.push({
          id: s.id,
          code_name_hospital: `${s.tafnit_code} > ${s.tafnit_name}`,
          code_name_clinical: `${s.icd9_code} > ${s.icd9_name}`,
          duration,
          default_surgery_duration: duration,
          side: s.side || "",
          is_primary: s.is_primary || "",
          additionalInfo: {
            tafnit_code: s.tafnit_code,
            tafnit_name: s.tafnit_name,
            icd9_code: s.icd9_code,
            icd9_name: s.icd9_name,
            duration_sec: s.duration,
            default_duration_sec: s.default_duration,
            body_side: s.side,
            insurance_id: s.insurance_id,
            insurance_name: s.insurance_name,
            item_code: s.item_code,
            name: s.name,
            medical_notes: s.medical_notes,
            is_primary: s.is_primary,
            expiration_date: s.expiration_date
          }
        });
      });

      commit("SET_SURGERY", {
        selected_surgery,
        initial_selected_surgery: JSON.parse(JSON.stringify(selected_surgery))
      });
    },

    /**
     * Creates new surgery orders via API
     * @param {Object} context
     * @param {Object} payload
     * @param {Array} payload.ordersToAdd - Orders to add (defaults to all selected surgeries)
     * @param {string} payload.surgeryId - Surgery ID to add orders to
     * @returns {Promise<boolean>} Success status
     */
    async setSurgeryOrders(
      { state, dispatch, getters },
      { ordersToAdd, surgeryId }
    ) {
      const orders = ordersToAdd || state.surgery.selected_surgery;
      if (!orders.length) return true;

      const formattedOrders = await dispatch("getFormattedOrders", {
        orders
      });

      for (const order of formattedOrders) {
        let orderData = order;

        // If anesthesia indication is required and order has secondary anesthesia code,
        // set duration to 00:00 (0 seconds)
        if (getters.isAnesthesiaIndicationRequired) {
          const hasSecondaryCode = ANESTHESIA_SECONDARY_CODES.some(code =>
            order.tafnit_code?.startsWith(code)
          );
          if (hasSecondaryCode) {
            orderData = { ...order, duration_sec: 0 };
          }
        }

        const success = await dispatch("callSurgeryOrdersAPI", {
          action: "create",
          surgery_id: surgeryId,
          data: orderData
        });

        if (!success) {
          return false;
        }
      }

      await dispatch("setInitialSurgeryOrders", surgeryId);
      // Format initial_selected_surgery to match getFormattedOrders format for proper comparison
      await dispatch("formatInitialSelectedSurgery");
      await dispatch("getImplants");
      return true;
    },

    /**
     * Formats initial_selected_surgery to match the format returned by getFormattedOrders.
     * This is required for proper comparison in editSurgeryOrders to detect actual changes.
     * Must be called after all edit mode initialization is complete (payType, insuranceSurgeryTemp, etc.)
     */
    async formatInitialSelectedSurgery({ state, commit, dispatch }) {
      if (!state.surgery.initial_selected_surgery?.length) return;

      const formattedInitial = [];
      for (let i = 0; i < state.surgery.initial_selected_surgery.length; i++) {
        const order = state.surgery.initial_selected_surgery[i];
        const formatted = (
          await dispatch("getFormattedOrders", {
            orders: [order],
            index: i
          })
        )[0];

        formattedInitial.push({
          ...formatted,
          id: order.id,
          additionalInfo: order.additionalInfo
        });
      }

      commit("SET_SURGERY", {
        initial_selected_surgery: formattedInitial
      });
    },

    /**
     * Edits existing surgery orders via API
     * Handles create, edit, and remove operations
     * @param {Object} context
     * @param {Object} payload
     * @param {string} payload.surgeryId - Surgery ID to edit orders for
     * @returns {Promise<boolean>} Success status
     */
    async editSurgeryOrders({ state, dispatch }, { surgeryId }) {
      if (!state.surgery.initial_selected_surgery?.length) return true;

      for (
        let index = 0;
        index < state.surgery.initial_selected_surgery.length;
        index++
      ) {
        const order = state.surgery.initial_selected_surgery[index];
        const currentOrder = state.surgery.selected_surgery[index];
        const orderFormatted = { ...order };
        delete orderFormatted.id;

        // Remove order
        if (!currentOrder) {
          const success = await dispatch("callSurgeryOrdersAPI", {
            action: "remove",
            order_id: order.id,
            surgery_id: surgeryId,
            data: orderFormatted
          });
          if (!success) {
            return false;
          }
          continue;
        }

        const currentOrderFormatted = (
          await dispatch("getFormattedOrders", {
            orders: [currentOrder],
            index
          })
        )[0];
        currentOrderFormatted.additionalInfo = order.additionalInfo;

        // No change
        if (
          JSON.stringify(orderFormatted) ===
          JSON.stringify(currentOrderFormatted)
        ) {
          continue;
        }

        let orderToEdit = { ...currentOrderFormatted.additionalInfo };
        delete currentOrderFormatted.additionalInfo;
        orderToEdit = { ...orderToEdit, ...currentOrderFormatted };

        if (orderToEdit.expiration_date) {
          orderToEdit.expiration_date = moment(
            orderToEdit.expiration_date
          ).format("YYYY-MM-DD");
        }

        // Edit order
        const success = await dispatch("callSurgeryOrdersAPI", {
          action: "edit",
          order_id: order.id,
          surgery_id: surgeryId,
          data: orderToEdit
        });
        if (!success) {
          return false;
        }
      }

      // Add new orders
      if (
        state.surgery.selected_surgery?.length >
        state.surgery.initial_selected_surgery?.length
      ) {
        for (let i = 0; i < state.surgery.selected_surgery.length; i++) {
          if (i <= state.surgery.initial_selected_surgery.length - 1) continue;

          const orderFormatted = await dispatch("getFormattedOrders", {
            orders: [state.surgery.selected_surgery[i]],
            index: i
          });

          const success = await dispatch("callSurgeryOrdersAPI", {
            action: "create",
            surgery_id: surgeryId,
            data: orderFormatted[0]
          });
          if (!success) {
            return false;
          }
        }
      }

      await dispatch("setInitialSurgeryOrders", surgeryId);
      // Format initial_selected_surgery to match getFormattedOrders format for proper comparison
      await dispatch("formatInitialSelectedSurgery");
      await dispatch("getImplants");
      return true;
    },

    // /**
    //  * Fetches implants for all selected surgeries
    //  * Removes duplicates based on implant ID
    //  */
    async getImplants({ state, commit, getters }) {
      commit("SET_IMPLANTS_LIST", []);

      if (!getters.hasImplantsTriggerCode) return;

      const promises = state.surgery.selected_surgery
        .map(s => {
          const [code] = s.code_name_hospital.split(" > ");
          return code;
        })
        .filter(s => s)
        .map(s => this._vm.$API.getImplantsBySurgery(s));

      const results = await Promise.all(promises);
      const allImplants = results.flatMap(implants => implants?.data || []);
      const uniqueImplants = Array.from(
        new Map(
          allImplants.map(implant => [implant.supplier_id, implant])
        ).values()
      );

      commit("SET_IMPLANTS_LIST", uniqueImplants);
    }
  }
};

export default Surgery;
